import Button, { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/Logout';
import { fetchAccount, logoutRedirect } from 'auth/AuthApi/authApi';
import { useAuth } from 'src/auth/hooks/useAuth';
import { palette } from 'theme/palette';
import LaunchIcon from '@mui/icons-material/Launch';

import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { addThemeParamsToUrl } from 'src/branding/utils/themeUrlBuilder';
import { envVariables } from 'src/common/utils/envVariables';
import { useTranslation } from 'react-i18next';
import { pxToRem } from 'src/common/utils/fonts';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';
import { autoScalingEnabledAtom } from 'src/atoms/ui/autoScaling';
import { useIsCslUser } from 'src/auth/hooks/useIsCslUser';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  height: theme.spacing(40),
  width: '100%',
  marginTop: theme.spacing(6),
  padding: 0,
  '& a': {
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(6, 16),
    span: {
      color: palette.grey[700],
    },
    '& svg': {
      color: palette.text.secondary,
    },
  },
  '&:hover': { backgroundColor: palette.grey[200], borderRadius: DEFAULT_BORDER_RADIUS },
}));

const LogoutMenuItemStyled = styled(MenuItem)(({ theme }) => ({
  height: theme.spacing(40),
  width: '99%',
  margin: theme.spacing(12, 1, 1, 1),
  boxShadow: `0px 0px ${theme.spacing(5)} 0px #00000014, 0px ${theme.spacing(1)} ${theme.spacing(
    2,
  )} 0px #00000014`,
  borderRadius: DEFAULT_BORDER_RADIUS,
  display: 'flex',
  justifyContent: 'center',
  '& span': { lineHeight: '0.9375rem' },
  '& svg': {
    color: palette.text.secondary,
    marginRight: theme.spacing(12),
  },
  '&:hover': {
    backgroundColor: palette.grey[200],
    borderRadius: DEFAULT_BORDER_RADIUS,
    boxShadow: `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)} 0px #00000005 inset`,
  },
}));

const UserButton = styled(Button)<ButtonProps>(({ theme }) => {
  return {
    fontSize: '0.75rem',
    padding: theme.spacing(10, 16),
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: theme.palette.grey[300],
      boxShadow: 'none',
    },
  };
});

export const UserMenu = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isCslUser = useIsCslUser();
  const autoScalingFeatureEnabled = useHasFeatureFlag('AutoScalingOff');
  const [isAutoScalingEnabled, setIsAutoScalingEnabled] = useAtom(autoScalingEnabledAtom);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: account } = useQuery({
    queryKey: ['Account', user.referenceId],
    queryFn: () => fetchAccount(user.referenceId),
    enabled: !!user.referenceId,
  });

  return (
    <div>
      <UserButton
        component="span"
        data-testid="account dropdown button"
        variant="contained"
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
      >
        {`${user.givenName} ${user.surname}`}
      </UserButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              width: '15.625rem',
              boxShadow: `0px 0px ${pxToRem(12)} 0px #0000001F`,
              overflow: 'visible',
              mt: 2,
              padding: 12,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -2,
                mr: 4,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ py: 0, px: 12 }} data-testid="account data">
          <Box>
            <Typography variant="text2" color="text.primary" data-testid="account name">
              {account?.accountName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="text5" color="text.primary" data-testid="account role">
              {user.role.replace('Customer ', '')}
            </Typography>
          </Box>
        </Box>
        <Divider
          sx={{
            pt: 4,
            mb: 12,
          }}
        />
        <MenuItemStyled data-testid="account change password" onClick={handleClose}>
          <a
            href={addThemeParamsToUrl(envVariables.VITE_KEYCLOAK_CHANGE_PASSWORD_URL)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="text1" color="text.primary">
              {t('common.changePassword')}
            </Typography>
            <LaunchIcon width={pxToRem(18)} height={pxToRem(18)} />
          </a>
        </MenuItemStyled>
        <MenuItemStyled data-testid="account user management" onClick={handleClose}>
          <a
            href={addThemeParamsToUrl(envVariables.VITE_KEYCLOAK_USER_MANAGEMENT_URL)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="text1" color="text.primary">
              {t('common.userManagement')}
            </Typography>
            <LaunchIcon width={pxToRem(18)} height={pxToRem(18)} />
          </a>
        </MenuItemStyled>

        {autoScalingFeatureEnabled && isCslUser && (
          <Box
            sx={{
              height: (theme) => theme.spacing(40),
              width: '100%',
              mt: (theme) => theme.spacing(6),
              px: (theme) => theme.spacing(16),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            data-testid="account auto-scaling toggle"
          >
            <Typography variant="text1" color="text.primary">
              {t('common.autoScaling')}
            </Typography>
            <Switch
              checked={isAutoScalingEnabled}
              onChange={(_, checked) => setIsAutoScalingEnabled(checked)}
              slotProps={{
                input: {
                  'aria-label': t('common.autoScaling'),
                },
              }}
            />
          </Box>
        )}

        <LogoutMenuItemStyled
          aria-label={t('common.logOut')}
          onClick={logoutRedirect}
          data-testid="account logout"
        >
          <LogoutIcon width={pxToRem(13)} height={pxToRem(18)} />
          <Typography
            variant="button"
            color="text.secondary"
            sx={{
              textTransform: 'none !important',
            }}
          >
            {t('common.logOut')}
          </Typography>
        </LogoutMenuItemStyled>
      </Menu>
    </div>
  );
};
